import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";
import {B2BinPayWalletsResponse, WithdrawCountryResponse} from "../../../types/Responses/WithdrawsResponse";
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import DropdownInput from "../../../components/DropdownInput";
import CountryAvatar from "../../../components/CountryAvatar";
import {CountryShort} from "../../../types/Responses/CountriesResponse";

interface B2BinPayFormProps {
    broker: number,
    clientTransactionID?: number
}

const B2BinPayForm = ({broker, clientTransactionID}: B2BinPayFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [country, setCountry] = useState<number>();
    const [email, setEmail] = useState<string>();
    const [address, setAddress] = useState<string>('');
    const [currency, setCurrency] = useState<number>();
    const [walletCurrency, setWalletCurrency] = useState<number>();
    const [countriesList, setCountriesList] = useState<WithdrawCountryResponse>();
    const [brokerWallets, setBrokerWallets] = useState<B2BinPayWalletsResponse>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setValidated(false);
        setCurrency(undefined);
        setWalletCurrency(undefined);
        setCountry(undefined);
        setAddress('');
        setWalletCurrency(undefined);
    }

    const getCountries = useCallback(() => {
        return new Promise((resolve, reject) => {
            API.getWithdrawCountries(broker, WithdrawProvider.B2BINPAY).then((response: AxiosResponse) => {
                setCountriesList(response.data as WithdrawCountryResponse);
                resolve(undefined);
            }).catch(e => {
                ErrorHandler(e, `get${APILink.GET_WITHDRAW_COUNTRIES}`, toast, handleError);
                reject(undefined);
            });
        });
    }, [toast, handleError]);

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [clientTransactionID, toast, handleError]);

    const getWallets = useCallback(() => {
        return new Promise((resolve, reject) => {
            API.getB2BinPayWallets(broker).then((response: AxiosResponse) => {
                setBrokerWallets(response.data as B2BinPayWalletsResponse);
                resolve(undefined)
            }).catch(e => {
                ErrorHandler(e, `get${APILink.GET_B2BINPAY_WALLETS.replace('$broker', broker.toString())}`, toast, handleError);
                reject(undefined);
            });
        });
    }, [toast, handleError]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);

    const changeCountry = useCallback((u: string | number) => {
        setCountry(typeof u === "string" ? parseInt(u) : u);
        setValidated(false);
    }, [setCountry]);

    const changeCurrency = useCallback((u: string | number) => {
        if (!brokerWallets || !brokerWallets.data)
            return ;
        const id = typeof u === "string" ? parseInt(u) : u;
        const selectedCurrency = brokerWallets.data.currencies.find(c => c.id === id);
        if (!selectedCurrency)
            return ;
        console.log(id);
        setCurrency(selectedCurrency.id);
        setWalletCurrency(selectedCurrency.wallet_currency);
        setValidated(false);
    }, [brokerWallets, setWalletCurrency, setCurrency]);

    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);


    const changeCryptoAddress = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAddress(e.currentTarget.value);
        }
    }, [setValidated, setAddress]);



    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [t, toast]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                amount,
                first_name: firstName,
                last_name: lastName,
                email,
                country,
                address,
                currency,
                wallet_currency: walletCurrency,
                broker,
                provider: WithdrawProvider.B2BINPAY,
            };
            API.makeB2BinPayWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_B2BINPAY_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [amount, firstName, lastName, walletCurrency, email, address, currency, country, broker, handleSuccessResponse, toast, handleError]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {key: 'currency', label: t('admin.withdraws.currency'), type: InputType.number},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^(?:(?:0[.,]\\d*[1-9]\\d*)|(?:[1-9]\\d{0,17}(?:[.,]\\d+)?))$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'country', label: t('admin.withdraws.country'), type: InputType.number},
        ];

        if (address.length) {
            inputs.push({key: 'address', label: t('admin.withdraws.crypto_wallet'), type: InputType.text, regex: '^.{5,}'});
        }

        const data = {
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            country,
            address,
            currency,
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [t, address, country, currency, amount, firstName, lastName, email, dispatch, callback]);


    const amountUI = useMemo(() => {
        console.log('AMOUNTUI', {
            country,
            currency,
            brokerWallets
        })
        if (!country || !currency || !brokerWallets || !brokerWallets.data)
            return <></>

        const selectedCurrency = brokerWallets.data.currencies.find(c => c.id === currency);
        if (!selectedCurrency)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: selectedCurrency.name})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [country, brokerWallets, currency, t, validated, amount, changeAmount]);

    const currenciesUI = useMemo(() => {
        if (!brokerWallets || !brokerWallets.data)
            return <></>

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={brokerWallets.data.currencies as FooterListItem[]}
                name={t('admin.withdraws.currency')}
                selected={currency}
                searchable={true}
                onChange={changeCurrency}/>
        </div>
    }, [t, brokerWallets, currency, changeCurrency]);

    const firstNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [t, validated, firstName, changeFirstName]);

    const countryUI = useMemo(() => {
        if (!countriesList)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={countriesList.data.map((c) => {
                    return {
                        name: c.name, id: c.id, icon: <CountryAvatar country={c as CountryShort} withName={false}
                                                                     className="h-3 max-w-[1rem]"/>
                    }
                })}
                name={t('admin.withdraws.country')} selected={country} searchable={true} onChange={changeCountry}/>
        </div>
    }, [country, changeCountry, countriesList]);

    const addressUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.crypto_address')} type="text" validated={validated} value={address}
                          fieldName="crypto_address"
                          onChange={changeCryptoAddress}/>
        </div>
    }, [t, validated, address, changeCryptoAddress]);


    const lastNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [t, validated, lastName, changeLastName]);

    const emailUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [t, validated, email, changeEmail])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit, t]);


    useEffect(() => {
        clearData();
        getWallets();
        getCountries();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID]);

    return <>
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {countryUI}
        {currenciesUI}
        {addressUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(B2BinPayForm);
